import React, { ReactNode, forwardRef, useContext } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { CustomSettings, Sort, UserSettingsContext } from '@eas/common-web';
import { PlannerContext } from '@modules/planner/planner-context';
import {
  getCalendarLegendWidth,
  settingsId,
  settingsVersion,
} from '@modules/planner/settings/settings';
import { CalendarContext } from '../../calendar-context';
import { CalendarDataType } from '../../calendar-types';
import { useStyles } from './legend-styles';
import { Unit } from './unit';

type LegendProps = {
  data: CalendarDataType[];
};

export const Legend = forwardRef<List, LegendProps>(function Legend(
  { data },
  ref
) {
  const classes = useStyles();
  const { settings } = useContext(CalendarContext);
  const legendInnerElementType = useLegendInnerElement({ data });

  return (
    <div
      className={classes.wrapper}
      style={{
        width: getCalendarLegendWidth(settings),
        marginTop: -1 * settings.headerHeight,
        height: `calc(100% - ${settings.headerHeight}px)`,
      }}
    >
      <AutoSizer disableWidth={true} style={{ height: '100%' }}>
        {({ height }) => (
          <>
            <List
              ref={ref}
              className={classes.legendList}
              layout="vertical"
              width="100%"
              height={height + settings.headerHeight}
              itemCount={data.length}
              itemSize={() => settings.rowHeight}
              itemData={data}
              innerElementType={legendInnerElementType}
            >
              {({ style, index, data }) => {
                const item = data[index];
                return <Unit key={item.id} item={item} style={style} />;
              }}
            </List>
          </>
        )}
      </AutoSizer>
    </div>
  );
});

type InnerElementProps = {
  children: ReactNode;
  [key: string]: unknown;
};

const switchSort = (field: string, sort: Sort): Sort => {
  return {
    ...sort,
    field,
    missing: sort.order === 'ASC' ? 'LAST' : 'FIRST',
    order: sort.order === 'ASC' ? 'DESC' : 'ASC',
  };
};

const useLegendInnerElement = ({ data }: { data: LegendProps['data'] }) => {
  const { settings, setSettings } = useContext(CalendarContext);
  const { calendarSource } = useContext(PlannerContext);
  const { setCustomSettings } = useContext(UserSettingsContext);
  const classes = useStyles();
  return function LegendInnerElement({ children, ...rest }: InnerElementProps) {
    return (
      <div
        style={{
          height: data.length * settings.rowHeight + settings.headerHeight,
        }}
        className={classes.legendInnerElement}
        {...rest}
      >
        <div
          className={classes.legendTitle}
          style={{ height: settings.headerHeight }}
        >
          <div
            style={{
              paddingLeft: 16,
              paddingTop: 8,
              position: 'absolute',
              zIndex: 1,
              top: 0,
              left: 0,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={settings.legendCollapsed}
                  size="small"
                  onChange={() => {
                    // save settings to user settings
                    setCustomSettings(settingsId, {
                      ...settings,
                      legendCollapsed: !settings.legendCollapsed,
                      version: settingsVersion,
                    } as CustomSettings);
                    // set current settings of calendar ref
                    setSettings((p) => ({
                      ...p,
                      legendCollapsed: !settings.legendCollapsed,
                    }));
                  }}
                  name="narrowLegend"
                  color="primary"
                />
              }
              label={
                <span style={{ fontSize: '12px' }}>Úsporné zobrazenie</span>
              }
            />
          </div>
          <div
            style={{
              flex: 3,
              alignItems: 'center',
              display: 'flex',
              cursor: 'pointer',
            }}
            onClick={() => {
              const legendSort = { ...settings.legendSort };
              legendSort.mobileUnit = switchSort('name', legendSort.mobileUnit);
              legendSort.technician = switchSort(
                'lastName',
                legendSort.technician
              );
              // save settings to user settings
              setCustomSettings(settingsId, {
                ...settings,
                legendSort,
                version: settingsVersion,
              } as CustomSettings);
              setSettings((p) => {
                return {
                  ...p,
                  legendSort,
                };
              });

              calendarSource?.yAxis?.refreshData?.();
            }}
          >
            <Typography variant="body1">Jednotka </Typography>
            {settings?.legendSort?.mobileUnit?.field === 'name' && (
              <>
                {settings?.legendSort?.mobileUnit?.order === 'ASC' ? (
                  <ArrowDropUpIcon fontSize="small" />
                ) : (
                  <ArrowDropDownIcon fontSize="small" />
                )}
              </>
            )}
          </div>
          {settings.legendCollapsed ? (
            <></>
          ) : (
            <>
              <div
                style={{
                  flex: 2,
                  alignItems: 'center',
                  display: 'flex',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const legendSort = { ...settings.legendSort };

                  legendSort.mobileUnit = switchSort(
                    'callFirst',
                    legendSort.mobileUnit
                  );
                  setCustomSettings(settingsId, {
                    ...settings,
                    legendSort,
                    version: settingsVersion,
                  } as CustomSettings);

                  setSettings((p) => {
                    return { ...p, legendSort };
                  });

                  calendarSource?.yAxis?.refreshData?.();
                }}
              >
                <Typography variant="body1">Volať ako prvého </Typography>
                {settings?.legendSort?.mobileUnit?.field === 'callFirst' && (
                  <>
                    {settings?.legendSort?.mobileUnit?.order === 'ASC' ? (
                      <ArrowDropUpIcon fontSize="small" />
                    ) : (
                      <ArrowDropDownIcon fontSize="small" />
                    )}
                  </>
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  alignItems: 'center',
                  display: 'flex',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const legendSort = { ...settings.legendSort };
                  legendSort.mobileUnit = switchSort(
                    'morningFailureSuitable',
                    legendSort.mobileUnit
                  );

                  setCustomSettings(settingsId, {
                    ...settings,
                    legendSort,
                    version: settingsVersion,
                  } as CustomSettings);

                  setSettings((p) => {
                    return { ...p, legendSort };
                  });

                  calendarSource?.yAxis?.refreshData?.();
                }}
              >
                <Typography variant="body1">Poruchy</Typography>
                {settings?.legendSort?.mobileUnit?.field ===
                  'morningFailureSuitable' && (
                  <>
                    {settings?.legendSort?.mobileUnit?.order === 'ASC' ? (
                      <ArrowDropUpIcon fontSize="small" />
                    ) : (
                      <ArrowDropDownIcon fontSize="small" />
                    )}
                  </>
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  alignItems: 'center',
                  display: 'flex',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const legendSort = { ...settings.legendSort };

                  legendSort.mobileUnit = switchSort(
                    'customerSuitable',
                    legendSort.mobileUnit
                  );
                  legendSort.technician = switchSort(
                    'customerSuitable',
                    legendSort.technician
                  );

                  setCustomSettings(settingsId, {
                    ...settings,
                    legendSort,
                    version: settingsVersion,
                  } as CustomSettings);

                  setSettings((p) => {
                    return { ...p, legendSort };
                  });

                  calendarSource?.yAxis?.refreshData?.();
                }}
              >
                <Typography variant="body1">Zákazník</Typography>
                {settings?.legendSort?.mobileUnit?.field ===
                  'customerSuitable' && (
                  <>
                    {settings?.legendSort?.mobileUnit?.order === 'ASC' ? (
                      <ArrowDropUpIcon fontSize="small" />
                    ) : (
                      <ArrowDropDownIcon fontSize="small" />
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div
          className={classes.legendInnerWrapper}
          style={{
            height: data.length * settings.rowHeight,
            minHeight: `calc(100% - ${settings.headerHeight}px)`,
          }}
        >
          {children}
        </div>
      </div>
    );
  };
};
